import type { SnackBarConfig } from '@shared/components/Snackbar';
import type {
  BaseReview,
  BasicReview,
  CurrentReview,
  FetchReviewsFiltersType,
  ReviewMeta,
} from '@shared/domain/review';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { FetchReviewsParameters } from '../../welcome/utils/api';
import { ReviewFilter, ReviewStatus, ReviewStep } from '@shared/domain/review';
import { useApi } from '@shared/services/api';
import { log } from '@shared/utils/log';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { UserContext, UserLoadState } from '@/shared/contexts/UserContext';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { fetchReviews } from '../../welcome/utils/api';
import ProfileSetupModal from '../components/ProfileSetupModal';

interface ReviewContextType {
  snackbarConfig: SnackBarConfig | undefined;
  setSnackbarConfig: Dispatch<SetStateAction<SnackBarConfig | undefined>>;
  reviewStep: ReviewStep;
  setReviewStep: Dispatch<SetStateAction<number>>;
  setLastStepSaved: Dispatch<SetStateAction<number>>;
  lastStepSaved: number | null;
  reviewId: number | null;
  setReviewId: Dispatch<SetStateAction<number | null>>;
  currentReview: CurrentReview;
  setCurrentReview: Dispatch<SetStateAction<BaseReview | null>>;
  handleServerError: () => void;
  handleFinishReview: (showSnackbar?: boolean, navigateToUrl?: string) => void;
  filteredReviews: Array<BasicReview> | null;
  reviewsMeta: ReviewMeta | null;
  fetchFilteredReviews: (filters: FetchReviewsFiltersType) => void;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  resetReviewState: () => void;
  clearStepCache: (step?: ReviewStep) => void;
  isReviewDialogOpen: boolean;
  setIsReviewDialogOpen: Dispatch<SetStateAction<boolean>>;
}

export const ReviewContext = createContext<ReviewContextType | undefined>(undefined);

export function useReviewContext() {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error('useReview must be used within a ReviewProvider');
  }
  return context;
}

const ReviewProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentReview, setCurrentReview] = useState<BaseReview | null>(null);
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();
  const [reviewStep, setReviewStep] = useState<number>(0);
  const [reviewId, setReviewId] = useState<number | null>(null);
  const [lastStepSaved, setLastStepSaved] = useState<number>(0);
  const [filteredReviews, setFilteredReviews] = useState<BasicReview[]>([]);
  const [reviewsMeta, setReviewsMeta] = useState<ReviewMeta | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState<boolean>(false);
  const prevReviewStepRef = useRef<number | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reviewStepCacheRef = useRef<Map<number, BaseReview>>(new Map());

  const { user, userLoadState, refetchUser } = useContext(UserContext);
  const { openMessage } = useMessageService();
  const api = useApi();

  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const redirectCompanyName = pathSegments[2];
  const redirectReviewId = pathSegments[3];

  const handleServerError = () => {
    setSnackbarConfig({
      type: 'error',
      message: 'An error has occurred, please try again later.',
      open: true,
    });

    setReviewId(null);
    setLastStepSaved(0);
    setReviewStep(0);
    setCurrentReview(null);
    setReviewStep(0);
  };

  const fetchFilteredReviews = useCallback(
    async (filters: FetchReviewsFiltersType): Promise<void> => {
      setLoading(true);
      try {
        const params: FetchReviewsParameters = {
          filter: ReviewFilter.CUSTOM,
          token: user?.idToken || '',
          limit: 100,
          ...filters,
        };
        const res = await fetchReviews(params);
        setFilteredReviews(res.data || []);
        setReviewsMeta(res.meta);
      } catch (error) {
        log.error('Error fetching filtered reviews:', error);
        handleServerError();
      } finally {
        setLoading(false);
      }
    },
    [user?.idToken],
  );

  const handleFinishReview = async (showSnackbar?: boolean, navigateToUrl?: string) => {
    if (showSnackbar) {
      setSnackbarConfig({
        type: 'success',
        message: 'Review successfully submitted, thank you!',
        open: true,
      });
    }

    setCurrentReview(null);
    setLastStepSaved(0);
    setReviewId(null);
    setReviewStep(0);
    reviewStepCacheRef.current.clear();

    if (showSnackbar && user?.id) {
      try {
        if (user?.email && user?.idToken) {
          await refetchUser({ email: user.email, idToken: user.idToken });
        }

        await new Promise((resolve) => setTimeout(resolve, 500));

        const reviewsResponse = await api.get('review', {
          params: {
            authorId: [user.id],
            searchType: 'custom',
          },
        });

        const allReviews = reviewsResponse?.data || [];

        const completedAndPendingReviews = allReviews.filter(
          (r: any) => r.status === ReviewStatus.COMPLETED || r.status === ReviewStatus.PENDING_APPROVAL,
        );

        const reviewCount = completedAndPendingReviews.length;
        const hasUsername = !!user?.username;
        const hasTagline = !!user?.tagLine;

        if (reviewCount === 1 && (!hasUsername || !hasTagline)) {
          openMessage({
            modalType: 'action',
            customBody: (
              <ProfileSetupModal
                onComplete={async () => {
                  if (user?.email && user?.idToken) {
                    await refetchUser({ email: user.email, idToken: user.idToken });
                  }
                }}
              />
            ),
            size: 'medium',
          });
        }
      } catch (error) {
        log.error('Error checking review count for profile setup modal:', error);
      }
    }

    if (navigateToUrl) {
      navigate(navigateToUrl);
    }

    // TODO: referral modal trigger lived here. Was driven by Pendo, which
    // was removed 2026-06-10. Wire to a replacement (in-app modal, separate
    // referral page, etc.) when the referral feature is rebuilt.
  };

  const getCurrentReview = async (key?: ReviewStep, signal?: AbortSignal) => {
    if (key !== undefined && reviewStepCacheRef.current.has(key)) {
      const cachedData = reviewStepCacheRef.current.get(key);
      if (cachedData) {
        setCurrentReview(cachedData);
        return;
      }
    }

    setLoading(true);

    try {
      const params = key !== undefined ? { key } : {};
      const response = await api.get(`review/${reviewId}`, { params, signal });

      if (signal?.aborted) {
        return;
      }

      if (response.error) {
        const status = response.status;

        if (status === 401) {
          setReviewId(null);
          navigate({
            pathname: '/sign-in',
            search: `?${createSearchParams({
              reviewId: String(redirectReviewId),
              companyName: redirectCompanyName,
              reAuth: 'true',
            })}`,
          });
          return;
        }

        if (status === 402) {
          navigate({
            pathname: `/company/${encodeURIComponent(redirectCompanyName)}`,
            search: `?${createSearchParams({
              checkReviewFromEmail: String(redirectReviewId),
            })}`,
          });
          return;
        }

        setSnackbarConfig({
          type: 'error',
          message: 'An error has occurred, please try again later.',
          open: true,
        });
        return;
      }

      setCurrentReview(response);

      if (key !== undefined && response) {
        reviewStepCacheRef.current.set(key, response);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      log.error('Error fetching review:', error);
      setSnackbarConfig({
        type: 'error',
        message: 'An error has occurred, please try again later.',
        open: true,
      });
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const clearStepCache = useCallback((step?: ReviewStep) => {
    if (step !== undefined) {
      reviewStepCacheRef.current.delete(step);
    } else {
      reviewStepCacheRef.current.clear();
    }
  }, []);

  const resetReviewState = useCallback(() => {
    setCurrentReview(null);
    setReviewStep(ReviewStep.BASIC_REVIEW);
    setReviewId(null);
    setLastStepSaved(0);
    setSnackbarConfig(undefined);
    setIsReviewDialogOpen(false);
    reviewStepCacheRef.current.clear();
  }, []);

  useEffect(() => {
    const prev = prevReviewStepRef.current;

    if (!reviewId || userLoadState !== UserLoadState.Authenticated) {
      setCurrentReview(null);
      prevReviewStepRef.current = reviewStep;
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const abortController = new AbortController();

    if (prev !== null && reviewStep < prev) {
      debounceTimerRef.current = setTimeout(() => {
        getCurrentReview(reviewStep, abortController.signal);
      }, 300);
    } else if (prev !== null && reviewStep > prev) {
      const isStepAlreadySaved = reviewStep <= (lastStepSaved || 0);

      if (isStepAlreadySaved) {
        debounceTimerRef.current = setTimeout(() => {
          getCurrentReview(reviewStep, abortController.signal);
        }, 300);
      } else {
        setCurrentReview(null);
      }
    }

    prevReviewStepRef.current = reviewStep;

    return () => {
      abortController.abort();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [reviewId, reviewStep, userLoadState, lastStepSaved]);

  const value = {
    currentReview,
    setCurrentReview,
    setSnackbarConfig,
    handleServerError,
    handleFinishReview,
    snackbarConfig,
    filteredReviews,
    fetchFilteredReviews,
    reviewsMeta,
    reviewId,
    setReviewId,
    reviewStep,
    setReviewStep,
    loading,
    setLoading,
    setLastStepSaved,
    lastStepSaved,
    resetReviewState,
    clearStepCache,
    isReviewDialogOpen,
    setIsReviewDialogOpen,
  };

  return <ReviewContext.Provider value={value}>{children}</ReviewContext.Provider>;
};
export default ReviewProvider;
